import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';
import LandingNav from './LandingNav';
import LandingFooter from './LandingFooter';
import SEO from '../../components/SEO';

const PrivacyPolicyPage = () => {
  const lastUpdated = 'February 16, 2026';
  const companyName = 'Smart POS';
  const companyEmail = 'privacy@smartpos.com';

  return (
    <div className="min-h-screen bg-primaryB-50">
      <SEO
        title="Privacy Policy"
        description="Privacy Policy for Smart POS – How we collect, use, and protect your data."
        path="/privacy"
      />
      <LandingNav />

      <section className="pt-24 sm:pt-32 pb-12">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold mb-6 transition-all duration-200 text-sm"
          >
            <ArrowLeft weight="bold" className="w-4 h-4" />
            Back to Home
          </Link>

          <h1 className="text-3xl sm:text-4xl font-extrabold text-primaryB-900 mb-2 tracking-tight">Privacy Policy</h1>
          <p className="text-primaryB-300 text-sm">Last updated: {lastUpdated}</p>
        </div>
      </section>

      <section className="pb-16 sm:pb-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="bg-primaryB-50 rounded-3xl border border-primaryB-200/60 p-6 sm:p-10">
            <div className="space-y-6 text-primaryB-700 text-sm leading-relaxed">

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">1. Introduction</h2>
                <p>{companyName} ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our point of sale and business management platform ("the Service").</p>
              </div>

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">2. Information We Collect</h2>

                <h3 className="font-bold text-primaryB-900 mt-4 mb-2">2.1 Information You Provide</h3>
                <p className="mb-3">When you register for and use the Service, we collect:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong className="text-primaryB-900">Account Information:</strong> Name, email address, phone number, business name, and login credentials</li>
                  <li><strong className="text-primaryB-900">Business Data:</strong> Products, inventory, pricing, sales transactions, and customer information you enter</li>
                  <li><strong className="text-primaryB-900">Staff Information:</strong> Names and roles of employees you add to your account</li>
                  <li><strong className="text-primaryB-900">Payment Information:</strong> Billing details for subscription payments (processed securely by our payment provider)</li>
                </ul>

                <h3 className="font-bold text-primaryB-900 mt-4 mb-2">2.2 Information Collected Automatically</h3>
                <p className="mb-3">When you use the Service, we automatically collect:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong className="text-primaryB-900">Usage Data:</strong> Features used, pages visited, and actions taken within the Service</li>
                  <li><strong className="text-primaryB-900">Device Information:</strong> Browser type, operating system, and device identifiers</li>
                  <li><strong className="text-primaryB-900">Log Data:</strong> IP address, access times, and referring URLs</li>
                </ul>
              </div>

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">3. How We Use Your Information</h2>
                <p className="mb-3">We use the information we collect to:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Provide, maintain, and improve the Service</li>
                  <li>Process transactions and send related information</li>
                  <li>Send daily sales reports and other notifications you've opted into</li>
                  <li>Respond to your inquiries and provide customer support</li>
                  <li>Monitor and analyze usage patterns to improve user experience</li>
                  <li>Detect, prevent, and address technical issues and fraud</li>
                  <li>Send important service updates and security alerts</li>
                </ul>
              </div>

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">4. Data Sharing and Disclosure</h2>
                <p className="mb-3">We do not sell your personal information. We may share your information only in the following circumstances:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong className="text-primaryB-900">Service Providers:</strong> With trusted third parties who assist us in operating the Service (e.g., cloud hosting, email delivery, payment processing)</li>
                  <li><strong className="text-primaryB-900">Legal Requirements:</strong> When required by law or to protect our rights and the safety of users</li>
                  <li><strong className="text-primaryB-900">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, with appropriate confidentiality protections</li>
                  <li><strong className="text-primaryB-900">With Your Consent:</strong> When you explicitly authorize us to share information</li>
                </ul>
              </div>

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">5. Data Security</h2>
                <p className="mb-3">We implement appropriate technical and organizational measures to protect your data, including:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Secure authentication and access controls</li>
                  <li>Regular security assessments and updates</li>
                  <li>Employee training on data protection</li>
                </ul>
                <p className="mt-3">While we strive to protect your information, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security of your data.</p>
              </div>

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">6. Data Retention</h2>
                <p className="mb-3">We retain your data for as long as your account is active or as needed to provide the Service. After account termination:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>You may request export of your data within 30 days</li>
                  <li>We will delete your data within 90 days, except where retention is required by law</li>
                  <li>Some anonymized, aggregated data may be retained for analytics purposes</li>
                </ul>
              </div>

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">7. Your Rights</h2>
                <p className="mb-3">You have the following rights regarding your personal data:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong className="text-primaryB-900">Access:</strong> Request a copy of your personal data</li>
                  <li><strong className="text-primaryB-900">Correction:</strong> Request correction of inaccurate data</li>
                  <li><strong className="text-primaryB-900">Deletion:</strong> Request deletion of your data (subject to legal requirements)</li>
                  <li><strong className="text-primaryB-900">Export:</strong> Request your data in a portable format</li>
                  <li><strong className="text-primaryB-900">Opt-out:</strong> Unsubscribe from marketing communications and daily reports</li>
                </ul>
                <p className="mt-3">To exercise these rights, contact us at {companyEmail}.</p>
              </div>

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">8. Cookies and Tracking</h2>
                <p className="mb-3">We use cookies and similar technologies to:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Keep you signed in to your account</li>
                  <li>Remember your preferences</li>
                  <li>Understand how you use the Service</li>
                  <li>Improve performance and user experience</li>
                </ul>
                <p className="mt-3">You can control cookies through your browser settings, but disabling them may affect Service functionality.</p>
              </div>

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">9. Third-Party Services</h2>
                <p>The Service may integrate with third-party services (e.g., payment processors, email providers). These services have their own privacy policies, and we encourage you to review them. We are not responsible for the privacy practices of third-party services.</p>
              </div>

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">10. Children's Privacy</h2>
                <p>The Service is not intended for users under 18 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.</p>
              </div>

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">11. International Data Transfers</h2>
                <p>Your data may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for such transfers in compliance with applicable data protection laws.</p>
              </div>

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">12. Changes to This Policy</h2>
                <p>We may update this Privacy Policy from time to time. We will notify you of significant changes via email or through the Service. Your continued use after changes take effect constitutes acceptance of the updated policy.</p>
              </div>

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">13. Contact Us</h2>
                <p>If you have questions about this Privacy Policy or our data practices, please contact us at: <strong className="text-primaryB-900">{companyEmail}</strong></p>
              </div>

            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default PrivacyPolicyPage;
